import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    const safeValue = Math.max(0, value);
    this.refs.cartBubbleCount.textContent =
      safeValue < 100 ? String(safeValue) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

    // Ensure bubble always exists on load
    this.refs.cartBubble.classList.remove('visually-hidden');
    this.refs.cartBubbleCount.classList.remove('hidden');

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles cart updates
   * @param {CartUpdateEvent} event
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm =
      event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Calculate new count
    const newCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.currentCartCount = Math.max(0, newCount);

    // 🔒 FORCE visibility (theme-safe)
    this.refs.cartBubble.classList.remove('visually-hidden');
    this.refs.cartBubbleCount.classList.remove('hidden');

    // Toggle cart icon state
    const hasItems = this.currentCartCount > 0;
    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      hasItems
    );

    // Animation only when adding items
    this.refs.cartBubble.classList.toggle(
      'cart-bubble--animating',
      hasItems && animate
    );

    // Save cart state
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    /**
     * 🚨 CRITICAL FIX
     * Theme scripts hide the bubble when cart is empty.
     * We re-apply "0" AFTER theme mutations.
     */
    if (!hasItems) {
      requestAnimationFrame(() => {
        this.refs.cartBubbleCount.textContent = '0';
        this.refs.cartBubbleCount.classList.remove('hidden');
        this.refs.cartBubble.classList.remove('visually-hidden');
      });
      return;
    }

    if (!animate) return;

    await onAnimationEnd(this.refs.cartBubble);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Sync count on reload / fast navigation
   */
  ensureCartBubbleIsCorrect = () => {
    const stored = sessionStorage.getItem('cart-count');
    if (!stored) return;

    try {
      const { value, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (!Number.isNaN(count)) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
